import { AccountModel } from '@ajs/models/account';
import AppStateService from '@ajs/services/AppStateService';
import { Injectable, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { getQueryParamFromHash } from '@app/core/functions/getQueryParamFromHash';
import { AccountFeatureFlag, BigCommerceSelfServiceFeatureFlag } from '@app/core/models/enums/account-feature-flag.enum';
import { SurfaceTier } from '@app/core/models/types/surface-tier.type';
import { combineLatest, filter, Subject, takeUntil, tap } from 'rxjs';

/**
 * This is a stateful service focusing ONLY on when restrict UI for BigCommerce Self Service [BC SS] mode.
 *
 * Users from BigCommerce are redirected to Feedonomics through a self-service app. We want to show a simplified user interface to ensure an easy-to-use experience.
 * Users can have a free plan or a paid plan - the paid plan gives them access to more features. These plans are set as an account level feature flag.
 * Note: Analyst accounts can toggle between BC SS mode by toggling their demo view.
 */
@Injectable({
    providedIn: 'root'
})
export class BigCommerceModeStateService implements OnDestroy {
    protected readonly unsubscribe$: Subject<void> = new Subject<void>();

    private featureFlagMode: BigCommerceSelfServiceFeatureFlag;

    private readonly _showRestrictedUI: WritableSignal<boolean>;
    public readonly showRestrictedUI: Signal<boolean>;

    private get account(): AccountModel | null {
        return this.appStateService.getAccount();
    }

    /**
     * Returns if the account has the Free or Paid feature flags.
     * This ignores any other use cases (ex: where an analyst is not in demo mode)
     */
    public get accountHasBcFeatureFlag(): boolean {
        return  this.featureFlagMode === BigCommerceSelfServiceFeatureFlag.FREE
            || this.featureFlagMode === BigCommerceSelfServiceFeatureFlag.PAID;
    }

    public get tier(): BigCommerceSelfServiceFeatureFlag {
        return this.featureFlagMode;
    }

    /**
     * Returns if the account showing restricted ui and the account has the paid feature flag
     */
    public get showBcSsFree(): boolean {
        return this.showRestrictedUI() && this.featureFlagMode === BigCommerceSelfServiceFeatureFlag.FREE;
    }

    /**
     * Returns if the account showing restricted ui and the account has the paid feature flag
     */
    public get showBcSsPaid(): boolean {
        return this.showRestrictedUI() && this.featureFlagMode === BigCommerceSelfServiceFeatureFlag.PAID;
    }

    constructor(
        private readonly appStateService: AppStateService
    ) {
        const defaultUiMode = this.hasHashParam();

        this._showRestrictedUI = signal(defaultUiMode);
        this.showRestrictedUI = this._showRestrictedUI.asReadonly();

        combineLatest([appStateService.userLoaded$, appStateService.accountLoaded$])
            .pipe(
                filter(([user, account]) => user !== null && account !== null),
                tap(() => {
                    this.featureFlagMode = this.getFeatureFlagMode();
                    const value: boolean = this.calculateShowRestrictedUI();
                    this._showRestrictedUI.set(value);
                }),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getFeatureFlagMode(): BigCommerceSelfServiceFeatureFlag {
        const isFree = this.account?.hasFeature(AccountFeatureFlag.BIGCOMMERCE_SELFSERVICE_UX, BigCommerceSelfServiceFeatureFlag.FREE);
        if (isFree) {
            return BigCommerceSelfServiceFeatureFlag.FREE;
        }

        const isPaid = this.account?.hasFeature(AccountFeatureFlag.BIGCOMMERCE_SELFSERVICE_UX, BigCommerceSelfServiceFeatureFlag.PAID);
        if (isPaid) {
            return BigCommerceSelfServiceFeatureFlag.PAID;
        }

        return BigCommerceSelfServiceFeatureFlag.NONE;
    }

    /**
     * This method determines if we should show the BigCommerce Self-Service UI mode. This is a limited set a features
     * to streamline the user experience for BigCommerce self-service users.
     *
     * This is turned on via an account level feature flag, and can be overwritten if the user is in demo mode, or the user's
     * privacy level is at least an Analyst.
     */
    private calculateShowRestrictedUI(): boolean {
        // if the bc_ui hash param is set then override and guarantee the bc ss ui
        if (this.hasHashParam()) {
            return true;
        }

        // If the account does not have the BC Self-Service mode feature flag enabled - do not show this corresponding UI
        if (this.featureFlagMode === BigCommerceSelfServiceFeatureFlag.NONE) {
            return false;
        }

        // If in demo mode, restrict features to just the BigCommerce self-service mode.
        const user = this.appStateService.getUser();
        if (user.isInDemoView()) {
            return true;
        }

        // If demo mode is not enabled, do not restrict analysts to BigCommerce Self-Service UI mode
        if (this.appStateService.isPrivacyLevelAtLeastAnalyst()) {
            return false;
        }

        return true;
    }

    /**
     * Gets the default UI mode based on the hash parameter `bc_ui`.
     * This is to default the UI to BC SS mode if the user is coming from the Gadget app.
     * This is a stopgap to supress this bug: https://feedonomics.atlassian.net/browse/FP-13440
     */
    private hasHashParam(): boolean {
        const param = getQueryParamFromHash('bc_ui');

        return param === 'true' || param === '1';
    }

    getCurrentTier(): SurfaceTier {
        switch (this.featureFlagMode) {
            case BigCommerceSelfServiceFeatureFlag.PAID:
                return 'retail';
            case BigCommerceSelfServiceFeatureFlag.FREE:
                return 'basic';
            case BigCommerceSelfServiceFeatureFlag.NONE:
            default:
                return undefined;
        }
    }

    getNextTier(): SurfaceTier {
        switch (this.featureFlagMode) {
            case BigCommerceSelfServiceFeatureFlag.PAID:
                return 'full-service'
            case BigCommerceSelfServiceFeatureFlag.FREE:
                return 'retail'
            case BigCommerceSelfServiceFeatureFlag.NONE:
            default:
                return undefined;
        }
    }
}
